import * as d3 from "d3";
import { RawData } from "./series_graph";
import { SeriesExerciseGraph } from "./series_exercise_graph";
import { themeState } from "state/Theme";
import { i18n } from "i18n/i18n";

interface BarData {
    exercise_id: string;
    status: string;
    cSum: number;
    count: number;
}

export class StackedStatusGraph extends SeriesExerciseGraph {
    protected readonly baseUrl = "/stats/stacked_status?series_id=";

    // Modified color scale based on the observable10 scheme with green and red as first colors
    private readonly observable10Modified = ["#3ca951", "#ff725c", "#efb118", "#6cc5b0", "#4269d0", "#ff8ab7", "#a463f2", "#97bbf5", "#9c6b4e", "#9498a0"];

    private data: BarData[];
    private maxSum: Record<string, number>; // Total number of submissions per exercise.

    /**
     * Draws the graph's SVG.
     *
     * @param {boolean} animation - Whether to play animations (disabled on resize redraw).
     */
    protected override draw(animation: boolean = true): void {
        super.draw(animation);

        const emptyColor = themeState.getCSSVariable("--d-off-surface");

        const xScale = d3
            .scaleLinear()
            .domain([0, 1])
            .range([0, this.innerWidth]);

        const colorScale = d3
            .scaleOrdinal()
            .range(this.observable10Modified)
            .domain(this.statusOrder);

        const tooltip = this.container
            .append("div")
            .attr("class", "d3-tooltip")
            .attr("pointer-events", "none")
            .style("opacity", 0)
            .style("z-index", 5);

        const legend = this.container
            .append("div")
            .attr("class", "legend")
            .style("margin-top", "-20px")
            .selectAll("div")
            .data(this.statusOrder)
            .enter()
            .append("div")
            .attr("class", "legend-item");

        legend
            .append("div")
            .attr("class", "legend-box")
            .style("background", (status: string) => colorScale(status) as string);

        legend
            .append("span")
            .attr("class", "legend-text")
            .text((status: string) => i18n.t(`js.status.${status.replaceAll(" ", "_")}`))
            .style("color", "currentColor")
            .style("font-size", `${this.fontSize}px`);

        // Draw bars for each data record.
        this.graph
            .selectAll(".bar")
            .data(this.data)
            .join("rect")
            .attr("class", "bar")
            .attr("x", 0)
            .attr("width", 0)
            .attr("y", (d: BarData) => this.y(d.exercise_id))
            .attr("height", this.y.bandwidth())
            .attr("fill", emptyColor)
            .on("mouseover", (_event: unknown, d: BarData) => {
                tooltip.transition().duration(200).style("opacity", 0.9);
                const percent = d3.format(".1%")(d.count / this.maxSum[d.exercise_id]);
                tooltip.html(`<b>${i18n.t(`js.status.${d.status.replaceAll(" ", "_")}`)}</b><br> ${percent} (${d.count}/${this.maxSum[d.exercise_id]})`);
            })
            .on("mousemove", (event: MouseEvent) => {
                const [xPos, yPos] = d3.pointer(event, this.svg.node());
                tooltip.style("left", `${xPos + 15}px`).style("top", `${yPos}px`);
            })
            .on("mouseout", () => {
                tooltip.transition().duration(500).style("opacity", 0);
            })
            .transition()
            .duration(animation ? 500 : 0)
            .attr("x", (d: BarData) => xScale(d.cSum / this.maxSum[d.exercise_id]))
            .attr("width", (d: BarData) => xScale(d.count / this.maxSum[d.exercise_id]))
            .attr("fill", (d: BarData) => colorScale(d.status) as string);

        const gridlines = this.graph
            .append("g")
            .attr("transform", `translate(0, ${this.y.bandwidth() / 2})`)
            .call(
                d3
                    .axisBottom(xScale)
                    .tickValues([0.2, 0.4, 0.6, 0.8])
                    .tickFormat(d3.format(".0%"))
                    .tickSize(this.innerHeight - this.y.bandwidth())
                    .tickSizeOuter(0)
            );

        gridlines.select(".domain").remove();
        gridlines.selectAll("line").style("stroke-dasharray", "3, 3");

        // Append total submission statistics to the right of the graph.
        const totalSubmissionStats = this.graph
            .append("g")
            .attr("transform", `translate(${this.innerWidth + 10}, 0)`);

        Object.entries(this.maxSum).forEach(([exerciseId, total]) => {
            totalSubmissionStats
                .append("text")
                .attr("x", (this.margin.right - 20) / 2)
                .attr("y", this.y(exerciseId) + this.y.bandwidth() / 2)
                .text(total)
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "central")
                .attr("fill", "currentColor")
                .style("font-size", "18px");
        });

        totalSubmissionStats
            .append("text")
            .attr("x", (this.margin.right - 20) / 2)
            .attr("y", 10)
            .text(`${i18n.t("js.total")} ${i18n.t("js.submissions")}`)
            .attr("text-anchor", "middle")
            .attr("fill", "currentColor")
            .style("font-size", `${this.fontSize}px`);
    }

    /**
     * Transforms raw server data into a format usable by the graph.
     *
     * @param {RawData} raw - The raw data containing submissions and exercise details.
     */
    protected override processData({ data, exercises }: RawData): void {
        // Extract exercise IDs and parse exercises.
        const exerciseIds = data.map(ex => ex.ex_id);
        this.parseExercises(exercises, exerciseIds);

        this.maxSum = {};
        this.data = [];

        // Process each exercise's data.
        data.forEach((exercise: { ex_id: number; ex_data: unknown[] }) => {
            const exerciseData = this.processExerciseData(exercise);
            this.data.push(...exerciseData.records);
            this.maxSum[exercise.ex_id] = exerciseData.total;
        });
    }

    /**
     * Processes a single exercise's data to calculate cumulative sums and create bar records.
     *
     * @param {{ ex_id: number, ex_data: unknown[] }} exercise - The exercise data from the server.
     * @returns {{ records: BarData[], total: number }} An object containing an array of bar records and the total submission count.
     */
    private processExerciseData(exercise: { ex_id: number; ex_data: unknown[] }): { records: BarData[]; total: number } {
        let runningSum = 0;
        const records: BarData[] = [];

        this.statusOrder.forEach((status: string) => {
            // Get count for the status; default to 0 if not present.
            const count = exercise.ex_data[status] ?? 0;
            records.push({
                exercise_id: String(exercise.ex_id),
                status,
                cSum: runningSum,
                count,
            });
            runningSum += count;
        });

        return { records, total: runningSum };
    }
}
